// Welche Aktionen ein LAUFENDER Signatur-Vorgang zulässt.
//
// Bis September 2026 bot die Oberfläche bei Status 'verschickt' gar nichts an: kein
// erneutes Senden, kein Zwischenstand, kein Selbst-Unterschreiben. Ging die Einladung
// unter (Ticket Björn: die Mail an das eigene Postfach war nicht mehr auffindbar), war
// der Vorgang tot und der Vertrag musste komplett neu erstellt werden.
//
// Die Regeln stehen hier statt in der Komponente, weil sie sich so ohne DOM prüfen
// lassen (Vitest läuft in diesem Projekt in der node-Umgebung). Der Server prüft
// dieselben Bedingungen NOCH EINMAL selbst — was hier steht, steuert nur, was die
// Oberfläche anbietet, und ist keine Sicherung.

export interface UnterzeichnerStand {
  name?: string | null;
  email?: string | null;
  status?: string | null;
}

export interface VorgangStand {
  status?: string | null;
  ist_vorlage?: boolean | null;
  unterzeichner?: UnterzeichnerStand[] | null;
}

/**
 * Der Unterzeichner, der an der Reihe ist — der ERSTE der Liste, der noch nicht
 * signiert hat.
 *
 * Bewusst nicht „irgendein offener": handlePostSignieren in der Edge Function lässt
 * nur den vordersten unterschreiben („Sie sind noch nicht an der Reihe"). Eine
 * Erinnerung an einen späteren Unterzeichner würde also auf eine Fehlermeldung führen.
 */
export function naechsterUnterzeichner(
  unterzeichner: UnterzeichnerStand[] | null | undefined,
): { index: number; unterzeichner: UnterzeichnerStand } | null {
  const liste = unterzeichner ?? [];
  const index = liste.findIndex((u) => u?.status !== 'signiert');
  return index === -1 ? null : { index, unterzeichner: liste[index] };
}

/** Laufender Vorgang, keine Vorlage — die gemeinsame Grundbedingung aller drei Aktionen. */
function istLaufend(v: VorgangStand): boolean {
  return v.status === 'verschickt' && !v.ist_vorlage;
}

/** Einladung erneut schicken: nur solange jemand offen ist UND eine Adresse hat. */
export function darfErneutSenden(v: VorgangStand): boolean {
  if (!istLaufend(v)) return false;
  const naechster = naechsterUnterzeichner(v.unterzeichner);
  return !!naechster && !!(naechster.unterzeichner.email || '').trim();
}

/**
 * Zwischenstand herunterladen. Auch erlaubt, wenn noch niemand signiert hat — dann
 * ist es eben das leere Formular, und genau das kann man ausdrucken und auf Papier
 * weiterführen. Beim abgeschlossenen Vorgang nicht: dafür gibt es das fertige PDF.
 */
export function darfZwischenstand(v: VorgangStand): boolean {
  return istLaufend(v);
}

/** Von Hand abschließen (auf Papier unterschrieben) — nur, was auch wirklich läuft. */
export function darfVonHandAbschliessen(v: VorgangStand): boolean {
  return istLaufend(v);
}

/** Namen der noch Ausstehenden für die Hinweisseite des Zwischenstands. */
export function fehlendeUnterzeichner(unterzeichner: UnterzeichnerStand[] | null | undefined): string[] {
  return (unterzeichner ?? [])
    .map((u, i) => ({ u, i }))
    .filter(({ u }) => u?.status !== 'signiert')
    // Ohne Namen und ohne Adresse bliebe sonst eine leere Zeile auf der Hinweisseite —
    // die Nummer ist die einzige Angabe, die es immer gibt.
    .map(({ u, i }) => (u?.name || '').trim() || (u?.email || '').trim() || `Unterzeichner ${i + 1}`);
}

/** Dateiname des Zwischenstands — im Download-Ordner nicht mit dem Original verwechselbar. */
export function zwischenstandDateiname(titel: string): string {
  const roh = (titel || '').trim() || 'Dokument';
  const ohneEndung = roh.replace(/\.pdf$/i, '').trim() || 'Dokument';
  const sauber = ohneEndung.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
  return `${sauber} (Zwischenstand).pdf`;
}
